/**
 * @fileoverview Acciones para la gestión y carga de funnels en el inbox.
 * Proporciona funciones para obtener, establecer y manejar errores de funnels usando el modelo FunnelsModelMC.
 * @module actions/inbox/funnelsActions
 * @category Actions Layer
 * @since 2.0.0
 * @version 3.1.0
 */

/**
 * @typedef {Object} Funnel
 * @description Estructura de datos para un funnel.
 * @property {string|number} id - Identificador único del funnel.
 * @property {string} name - Nombre del funnel.
 * @property {Array<Object>} [steps] - Pasos del funnel.
 */

/**
 * Tipos de acción para la gestión de funnels.
 * @constant
 * @type {Object}
 * @property {string} GET_FUNNELS_START - Acción para iniciar la carga de funnels.
 * @property {string} SET_FUNNELS - Acción para establecer los funnels cargados.
 * @property {string} GET_FUNNELS_ERROR - Acción para manejar errores en la carga de funnels.
 * @property {string} FUNNELS_ALREADY_LOADED - Acción cuando los funnels ya están cargados.
 */
import FunnelsModelMC from '../../presentation/models/FunnelsModelMC';

// Tipos de acción igual que funnels.js
export const FUNNELS_ACTIONS = {
  GET_FUNNELS_START: 'GET_FUNNELS_START',
  SET_FUNNELS: 'SET_FUNNELS',
  GET_FUNNELS_ERROR: 'GET_FUNNELS_ERROR',
  FUNNELS_ALREADY_LOADED: 'FUNNELS_ALREADY_LOADED',
};

/**
 * Acción asíncrona para obtener funnels siempre que no estén en estado de carga.
 *
 * @function getFunnelsAlways
 * @category Actions Layer
 * @param {Object} params - Parámetros para la consulta de funnels.
 * @returns {Promise<void>} No retorna valor, solo despacha acciones.
 * @example
 * await getFunnelsAlways({ status: 'active' })(dispatch, getState);
 * @see {@link FUNNELS_ACTIONS}
 */
export const getFunnelsAlways = (params) => async (dispatch, getState) => {
  const { loadingFunnels } = getState().funnelsReducer;
  if (loadingFunnels) return;
  dispatch({ type: FUNNELS_ACTIONS.GET_FUNNELS_START });
  try {
    const data = await FunnelsModelMC.get(params);
    dispatch({ type: FUNNELS_ACTIONS.SET_FUNNELS, data });
  } catch (err) {
    dispatch({ type: FUNNELS_ACTIONS.GET_FUNNELS_ERROR, error: err.message || 'Error loading funnels' });
  }
};

/**
 * Acción asíncrona para obtener funnels solo si el array está vacío y no se está cargando.
 *
 * @function getFunnelsIfEmpty
 * @category Actions Layer
 * @param {Object} params - Parámetros para la consulta de funnels.
 * @returns {Promise<void>} No retorna valor, solo despacha acciones.
 * @example
 * await getFunnelsIfEmpty({ status: 'active' })(dispatch, getState);
 * @see {@link FUNNELS_ACTIONS}
 */
export const getFunnelsIfEmpty = (params) => async (dispatch, getState) => {
  const { funnels, loadingFunnels } = getState().funnelsReducer;
  if (loadingFunnels) return;
  if (!funnels || !funnels.length) {
    dispatch({ type: FUNNELS_ACTIONS.GET_FUNNELS_START });
    try {
      const data = await FunnelsModelMC.get(params);
      dispatch({ type: FUNNELS_ACTIONS.SET_FUNNELS, data });
    } catch (err) {
      dispatch({ type: FUNNELS_ACTIONS.GET_FUNNELS_ERROR, error: err.message || 'Error loading funnels' });
    }
  } else {
    dispatch({ type: FUNNELS_ACTIONS.FUNNELS_ALREADY_LOADED });
  }
};
